import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth, formatApiError } from "@/lib/auth";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Seo } from "@/components/seo/Seo";

export const Register = () => {
  const nav = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    clinic_name: "", license_number: "", country: "", phone: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      nav("/account?welcome=1");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Apply for B2B clinic account"
        description="Apply for a verified wholesale account. Reserved for licensed clinics and medical professionals. We review every application within 24 hours."
        path="/register"
      />
      <Header />
      <main className="max-w-2xl mx-auto px-6 pt-28 md:pt-32 pb-24" data-testid="register-page">
        <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">B2B Application</div>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight leading-[1.05] mb-3">
          <span className="font-black">Apply for </span><span className="font-thin">wholesale access.</span>
        </h1>
        <p className="text-sm text-neutral-600 mb-10 max-w-xl font-light">
          Discount Fillers is reserved for licensed clinics and medical professionals. We review and verify every account within 24 hours of submission.
        </p>

        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Full name" testid="reg-name" value={form.name} onChange={set("name")} required />
            <Field label="Email" testid="reg-email" type="email" value={form.email} onChange={set("email")} required />
            <Field label="Password (min 8)" testid="reg-password" type="password" value={form.password} onChange={set("password")} required minLength={8} />
            <Field label="Phone" testid="reg-phone" value={form.phone} onChange={set("phone")} required />
            <Field label="Clinic / Company name" testid="reg-clinic" value={form.clinic_name} onChange={set("clinic_name")} required wide />
            <Field label="Medical license #" testid="reg-license" value={form.license_number} onChange={set("license_number")} required />
            <Field label="Country" testid="reg-country" value={form.country} onChange={set("country")} required />
          </div>
          {error && <div className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2" data-testid="reg-error">{error}</div>}

          <div className="text-xs text-neutral-500 leading-relaxed font-light pt-2">
            By applying you confirm that you are a credentialed medical professional. We may request a copy of your license before unlocking purchase.
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-black text-white text-sm font-medium hover:bg-neutral-800 disabled:opacity-60"
            data-testid="reg-submit"
          >
            {loading ? "Submitting..." : "Submit application"}
          </button>
        </form>

        <p className="mt-6 text-sm text-neutral-600">
          Already have an account? <Link to="/login" className="border-b border-black pb-0.5 font-medium" data-testid="reg-to-login">Sign in →</Link>
        </p>
      </main>
      <Footer />
    </>
  );
};

const Field = ({ label, testid, wide, ...rest }) => (
  <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
    <span className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium">{label}</span>
    <input
      {...rest}
      className="mt-1 w-full border border-neutral-300 px-4 py-3 text-sm focus:outline-none focus:border-black"
      data-testid={testid}
    />
  </label>
);
